import React from 'react';
import {
  Box,
  SimpleGrid,
  Text,
  Badge,
  useColorModeValue,
  Heading,
  VStack,
  Spinner,
  Tooltip,
  HStack, // Add this import
} from '@chakra-ui/react';

interface Market {
  market_type: string;
  region: string;
  primary_exchanges: string;
  local_open: string;
  local_close: string;
  current_status: string;
  notes: string;
}

export const MarketStatus: React.FC<{ markets: Market[] }> = ({ markets }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'green';
      case 'closed':
        return 'red';
      default:
        return 'gray';
    }
  };

  const groupedMarkets = markets.reduce((acc, market) => {
    if (!acc[market.market_type]) {
      acc[market.market_type] = [];
    }
    acc[market.market_type].push(market);
    return acc;
  }, {} as Record<string, Market[]>);

  return (
    <VStack spacing={6} align="stretch" w="full">
      {Object.entries(groupedMarkets).map(([type, markets]) => (
        <Box key={type}>
          <Heading size="md" mb={4}>{type} Markets</Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {markets.map((market, idx) => (
              <Tooltip key={idx} label={market.notes} isDisabled={!market.notes}>
                <Box
                  p={4}
                  bg={bgColor}
                  borderRadius="lg"
                  border="1px"
                  borderColor={borderColor}
                  boxShadow="sm"
                  _hover={{ transform: 'translateY(-2px)', transition: 'transform 0.2s' }}
                >
                  <VStack align="stretch" spacing={2}>
                    <Text fontWeight="bold">{market.region}</Text>
                    <Text fontSize="sm" color="gray.500" noOfLines={2}>
                      {market.primary_exchanges}
                    </Text>
                    <HStack justify="space-between">
                      <Text fontSize="sm">
                        {market.local_open} - {market.local_close}
                      </Text>
                      <Badge colorScheme={getStatusColor(market.current_status)}>
                        {market.current_status.toUpperCase()}
                      </Badge>
                    </HStack>
                  </VStack>
                </Box>
              </Tooltip>
            ))}
          </SimpleGrid>
        </Box>
      ))}
    </VStack>
  );
};
